import { useState } from 'react';
import { buildShareUrl, defaultTemplate, SHARE_TITLE } from './shareLinks';

// Reusable "Share your link" card. Shown on the post-signup success screen and
// on the portal, so a referrer always has a ready-to-send message that carries
// their tracked `?ref=` link — the only thing that actually gets them paid.
//
// Renders nothing without a link (e.g. portal still loading), so callers can mount
// it unconditionally.

// Inline brand icons (no icon dependency). 24×24, fill=currentColor.
const ICONS = {
  whatsapp: (
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  ),
  facebook: (
    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.898v-2.89h2.54V9.797c0-2.507 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562v1.875h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
  ),
  x: (
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  ),
  telegram: (
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.061 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.788.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  ),
  email: (
    <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Zm21-1.762V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
  ),
  sms: (
    <path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM7 9h10v2H7V9zm0 4h7v2H7v-2z" />
  ),
};

const NETWORKS = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'x', label: 'X' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
];

export default function ShareKit({ referralUrl }) {
  // Lazy init: the parent only mounts ShareKit once it has a link, so the
  // template is built with the link from the first render.
  const [message, setMessage] = useState(() => defaultTemplate(referralUrl));
  const [copied, setCopied] = useState('');

  if (!referralUrl) return null;

  const copy = (text, which) => {
    navigator.clipboard?.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(''), 1500);
  };

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const nativeShare = async () => {
    try {
      await navigator.share({ title: SHARE_TITLE, text: message });
    } catch {
      // user dismissed the share sheet — nothing to do
    }
  };

  return (
    <div className="card">
      <h2>Share your link</h2>

      <div className="share-note">
        <b>Who to share with:</b> send your link to anyone who already has a LinkedIn
        account that's <b>over 6 months old</b> — friends, family, colleagues. They earn{' '}
        <b>£20</b>, you earn <b>£10</b>, both paid within 24 hours of completion.
        <div className="share-note-warn">
          <span aria-hidden="true">⚠️</span> Only people who sign up by{' '}
          <b>clicking your link</b> are tracked and paid — so always share the link
          itself, not just your name.
        </div>
      </div>

      <label className="share-label" htmlFor="share-message">
        Your message — copy it or edit it; your link is already included:
      </label>
      <textarea
        id="share-message"
        className="share-textarea"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={6}
      />

      <div className="share-copy-row">
        <button type="button" className="btn-ghost" onClick={() => copy(message, 'msg')}>
          {copied === 'msg' ? 'Copied!' : 'Copy message'}
        </button>
        <button type="button" className="btn-ghost" onClick={() => copy(referralUrl, 'link')}>
          {copied === 'link' ? 'Copied!' : 'Copy link only'}
        </button>
        {canNativeShare && (
          <button type="button" className="btn-ghost" onClick={nativeShare}>
            Share…
          </button>
        )}
        {/* Announce copy success to screen readers (the visible label change alone
            isn't reliably announced). */}
        <span className="sr-only" role="status" aria-live="polite">
          {copied === 'msg' ? 'Message copied to clipboard' : copied === 'link' ? 'Link copied to clipboard' : ''}
        </span>
      </div>

      <div className="share-actions">
        {NETWORKS.map((n) => (
          <a
            key={n.key}
            className={`share-btn share-${n.key}`}
            href={buildShareUrl(n.key, message, referralUrl)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              {ICONS[n.key]}
            </svg>
            <span>{n.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
